'use client'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DocsSidebar, DocsSidebarMobileBar } from './DocsSidebar'

const SIDEBAR_STORAGE_KEY = 'sidebar_collapsed'

function readPersistedSidebarState() {
  if (typeof window === 'undefined') return true

  const localValue = window.localStorage.getItem(SIDEBAR_STORAGE_KEY)
  if (localValue === 'true') return true
  if (localValue === 'false') return false

  const cookieMatch = document.cookie.match(/(?:^|;\s*)sidebar_collapsed=([^;]+)/)
  if (!cookieMatch) return true

  return cookieMatch[1] === 'true'
}

export function DocsShell({ banner, navbar, footer, children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const headerRef = useRef(null)

  useEffect(() => {
    setIsSidebarOpen(readPersistedSidebarState())
  }, [])

  useEffect(() => {
    const header = headerRef.current
    if (!header) return

    const root = document.documentElement

    // Nextra's <Mermaid> re-renders on every <html> attribute mutation and empties its own container
    // while doing so, so redundant writes here blank rendered diagrams. Keep <html> writes to a minimum;
    // the scrolled flag lives on <body> for the same reason. The vars stay here because
    // `html { scroll-padding-top }` in globals.css reads them.
    const setRootVar = (name: string, value: string) => {
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value)
    }

    const update = () => {
      // Calculate top part height (banner + logo row)
      const aiToolbarHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--supra-ai-toolbar-height') || '44',
      )

      const isMobile = window.innerWidth < 1024

      // The links row is the full-width second row of the navbar (desktop only)
      const linksRow = header.querySelector('.nextra-navbar nav > div:has(> a[href])')

      if (isMobile) {
        const stickyTop = 0
        setRootVar('--supra-header-sticky-top', `${stickyTop}px`)
        setRootVar('--supra-sticky-header-bottom', `${stickyTop + header.offsetHeight}px`)
      } else if (linksRow) {
        const headerRect = header.getBoundingClientRect()
        const linksRect = linksRow.getBoundingClientRect()
        const topPartHeight = linksRect.top - headerRect.top
        const stickyTop = aiToolbarHeight - topPartHeight - 7 // Shift up 7px more as requested by user

        // Stick the header when the links row reaches the AI toolbar
        setRootVar('--supra-header-sticky-top', `${stickyTop}px`)

        // The bottom of the sticky header is (stickyTop + totalHeight)
        setRootVar('--supra-sticky-header-bottom', `${stickyTop + header.offsetHeight}px`)
      } else {
        // Fallback to sticking at the AI toolbar
        setRootVar('--supra-header-sticky-top', `${aiToolbarHeight}px`)
        setRootVar('--supra-sticky-header-bottom', `${aiToolbarHeight + header.offsetHeight}px`)
      }

      setRootVar('--supra-header-height', `${header.offsetHeight}px`)
    }

    const handleScroll = () => {
      const scrolled = window.scrollY > 20 ? 'true' : 'false'
      if (document.body.dataset.scrolled !== scrolled) document.body.dataset.scrolled = scrolled
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', update, { passive: true })
    handleScroll()

    const observer = new ResizeObserver(update)
    observer.observe(header)
    update()
    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', update)
    }
  }, [])

  const handleSidebarOpenChange = useCallback((nextOpen) => {
    setIsSidebarOpen(nextOpen)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(nextOpen))
    }
  }, [])

  return (
    <SidebarProvider
      open={isSidebarOpen}
      onOpenChange={handleSidebarOpenChange}
      className="
        w-full flex flex-col
        [--sidebar-width:300px]
        [--sidebar-width-icon:4rem]
        lg:[--sidebar-width-icon:6rem]
      "
    >
      <div className="sticky z-50 w-full" ref={headerRef} style={{ top: 'var(--supra-header-sticky-top, 0px)' }}>
        {banner ? <div>{banner}</div> : null}
        {navbar ? (
          <div className="relative w-full flex items-center">
            {/* <SidebarTrigger className="absolute md:hidden flex z-99 border border-accent-foreground/15 dark:bg-black bg-white right-7 left-[calc(100%-4rem)] md:right-0 md:left-3" /> */}
            {navbar}
          </div>
        ) : null}
      </div>
      <div className="flex flex-1 min-h-[calc(100dvh-var(--supra-header-height,0px))] items-start w-full">
        <DocsSidebar />
        <SidebarInset className="relative top-auto max-h-none overflow-visible pt-0 min-w-0 max-w-none m-0">
          <DocsSidebarMobileBar />
          <div className="flex w-full min-w-0 flex-1 px-0 bg-background">{children}</div>
          {footer ? <div className="w-full">{footer}</div> : null}
        </SidebarInset>
      </div>
    </SidebarProvider>
  )
}
